/**
 * Helpers for `/puzzles/sliding` browse cards and play page copy.
 */

import { DIFF_LEVELS, templateForGridAndLevel } from "../../admin/story/adminSlidingUi";

/** Matches {@link SlidingPuzzlePlay} default template priority so shelf thumbnails match play. */
const BROWSE_PREVIEW_SIZE_OPTIONS = [4, 6];

function resolveBrowsePreviewGridLevel(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    if (templateForGridAndLevel(templates, 4, "EASY")) {
        return { gridSize: 4, level: "EASY" };
    }
    if (templateForGridAndLevel(templates, 4, "MEDIUM")) {
        return { gridSize: 4, level: "MEDIUM" };
    }
    if (templateForGridAndLevel(templates, 6, "MEDIUM")) {
        return { gridSize: 6, level: "MEDIUM" };
    }
    for (const gs of BROWSE_PREVIEW_SIZE_OPTIONS) {
        for (const level of DIFF_LEVELS) {
            if (templateForGridAndLevel(templates, gs, level)) {
                return { gridSize: gs, level };
            }
        }
    }
    return null;
}

function firstTemplateWithMatchingLayout(templates) {
    if (!Array.isArray(templates)) return null;
    for (const t of templates) {
        const g = Number(t?.gridSize);
        const n = Number.isFinite(g) && g > 0 ? g * g : 0;
        const layout = t?.startingLayout ?? t?.starting_layout;
        if (n > 0 && Array.isArray(layout) && layout.length === n) return { tpl: t, gridSize: g };
    }
    return null;
}

/**
 * Board preview for browse cards: same image + grid + `starting_layout` as the default play template.
 * @returns {{ imageUrl: string, gridSize: number, startingLayout: number[] } | null}
 */
/**
 * Story id, title, and default play preview (Easy 4×4 when available) for landing hero.
 * @returns {{ storyId: string, title: string, gridSize: number, level: string, imageUrl: string | null, startingLayout: number[] | null } | null}
 */
export function getSlidingLandingFeaturedMeta(group) {
    const storyId = getSlidingGroupStoryId(group);
    if (!storyId) return null;
    const story = group?.story ?? null;
    const title = getSlidingStoryTitle(story) || storyId;
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    const sel = resolveBrowsePreviewGridLevel(templates);
    const preview = pickSlidingBrowseBoardPreview(group);
    const gridSize = Number(sel?.gridSize ?? preview?.gridSize ?? 4) || 4;
    const level = sel?.level ?? "MEDIUM";
    const imageUrl = preview?.imageUrl ?? pickSlidingCardImageUrl(story);
    const startingLayout = preview?.startingLayout ?? null;
    return {
        storyId,
        title,
        gridSize,
        level,
        imageUrl: imageUrl && String(imageUrl).trim() ? String(imageUrl).trim() : null,
        startingLayout: Array.isArray(startingLayout) ? startingLayout : null,
        publishedAt: getSlidingGroupPublishedAt(group),
    };
}

export function pickSlidingBrowseBoardPreview(group) {
    const story = group?.story;
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    const sel = resolveBrowsePreviewGridLevel(templates);
    let tpl = sel ? templateForGridAndLevel(templates, sel.gridSize, sel.level) : null;
    let gs = sel ? Number(sel.gridSize) : 0;

    if (!tpl) {
        const fb = firstTemplateWithMatchingLayout(templates);
        if (!fb) return null;
        tpl = fb.tpl;
        gs = fb.gridSize;
    }

    const layout = tpl.startingLayout ?? tpl.starting_layout;
    const n = gs > 0 ? gs * gs : 0;
    if (!Array.isArray(layout) || layout.length !== n || n <= 0) return null;
    const imageUrl = pickSlidingPlayImageUrl(story) || pickSlidingCardImageUrl(story);
    if (!imageUrl || !String(imageUrl).trim()) return null;
    return {
        imageUrl: String(imageUrl).trim(),
        gridSize: gs,
        startingLayout: layout.map((x) => Number(x)),
    };
}

export function getSlidingGroupStoryId(group) {
    if (!group || typeof group !== "object") return null;
    const sid = group.story?.id ?? group.storyId;
    return typeof sid === "string" && sid.trim() ? sid.trim() : null;
}

/** Prefer English title, then French (`english`/`french` or legacy `en`/`fr`). */
export function getSlidingStoryTitle(story) {
    if (!story || typeof story !== "object") return "";
    const en = story.english?.title ?? story.en?.title;
    const fr = story.french?.title ?? story.fr?.title;
    const enStr = typeof en === "string" ? en.trim() : "";
    const frStr = typeof fr === "string" ? fr.trim() : "";
    if (enStr) return enStr;
    if (frStr) return frStr;
    return "";
}

/** Story thumbnail for compact UI (200px icon only). */
export function pickSlidingStoryIcon200Url(story) {
    if (!story || typeof story !== "object") return null;
    const u = story.imageIcon200Url ?? story.image_icon_200_url;
    return typeof u === "string" && u.trim() ? u.trim() : null;
}

/** Toolbar / dialog preview — prefer 200px icon, not 400px. */
export function pickSlidingToolbarPreviewImageUrl(story) {
    const icon200 = pickSlidingStoryIcon200Url(story);
    if (icon200) return icon200;
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imagePreviewUrl",
        "image_preview_url",
        "imageUrl",
        "image_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Browse cards: prefer 400px icon, then fallbacks. */
export function pickSlidingCardImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imageIcon400Url",
        "image_icon_400_url",
        "imageIcon200Url",
        "image_icon_200_url",
        "imagePreviewUrl",
        "image_preview_url",
        "imageUrl",
        "image_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Play surface: full-resolution image for slicing (same aspect as reference). */
export function pickSlidingPlayImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imageUrl",
        "image_url",
        "imagePreviewUrl",
        "image_preview_url",
        "imageIcon400Url",
        "image_icon_400_url",
        "imageIcon200Url",
        "image_icon_200_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Best original/source image for a sliding story (solve reveal, toolbar preview). */
export function pickSlidingOriginalImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = ["imageUrl", "image_url", "imagePreviewUrl", "image_preview_url"];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return pickSlidingStoryIcon200Url(story) || pickSlidingPlayImageUrl(story);
}

/** True when the authenticated user has completed this sliding template (home API `completedByUser`). */
export function isSlidingTemplateCompletedByUser(template) {
    if (!template || typeof template !== "object") return false;
    const flag = template.completedByUser ?? template.completed_by_user;
    if (flag === true) return true;
    if (flag == null || flag === false) return false;
    if (typeof flag === "number") return flag > 0;
    if (typeof flag === "string") return flag.trim() !== "";
    return Boolean(flag);
}

/**
 * Browse chips: free/paid tier comes from `storySliding.free` (bundle row) in the new shape.
 * Falls back to scanning per-template `free` for legacy data.
 * @returns {{ hasFree: boolean, hasStandard: boolean }}
 */
export function getSlidingGroupTierFlags(group) {
    // New shape: storySliding carries the authoritative free flag
    const ss = group?.storySliding;
    if (ss && typeof ss === "object") {
        const isFree = ss.free === true;
        return { hasFree: isFree, hasStandard: !isFree };
    }
    // Legacy fallback: scan per-template free flags
    const templates = group?.templates;
    if (!Array.isArray(templates) || templates.length === 0) {
        return { hasFree: false, hasStandard: false };
    }
    let hasFree = false;
    let hasStandard = false;
    for (const t of templates) {
        if (t?.free === true) hasFree = true;
        else hasStandard = true;
    }
    return { hasFree, hasStandard };
}

/**
 * Shelf NEW / published caption.
 * New shape: publishedAt lives on `storySliding` (bundle row).
 * Falls back to per-template or story fields for legacy data.
 */
export function getSlidingGroupPublishedAt(group) {
    // New shape: storySliding carries publishedAt
    const ss = group?.storySliding;
    if (ss && typeof ss === "object") {
        const fromSs = ss.publishedAt ?? ss.published_at ?? null;
        if (fromSs != null && String(fromSs).trim() !== "") return fromSs;
    }
    // Legacy fallback
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    const first = templates[0];
    const fromFirst = first?.publishedAt ?? first?.published_at ?? null;
    if (fromFirst != null && String(fromFirst).trim() !== "") return fromFirst;
    const story = group?.story;
    return story?.publishedAt ?? story?.published_at ?? null;
}

/**
 * Shape expected by {@link PuzzleCardBase} / {@link SlidingCard} for `/puzzles/sliding` shelves.
 * `free === true` when storySliding.free is true (new shape) or any template is free (legacy).
 */
export function homeSlidingGroupToCardPuzzle(group) {
    const storyId = getSlidingGroupStoryId(group);
    if (!storyId) return null;
    const story = group?.story ?? null;
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    const title = getSlidingStoryTitle(story) || storyId;
    // New shape: free is authoritative on storySliding; legacy: scan templates
    const ss = group?.storySliding;
    const hasFree = ss && typeof ss === "object"
        ? ss.free === true
        : templates.some((t) => t?.free === true);
    const publishedAt = getSlidingGroupPublishedAt(group);
    const genre =
        story?.topic?.genre?.name ??
        templates[0]?.topic?.genre?.name ??
        "default";
    const tplTags = templates[0]?.tags;
    const tags = Array.isArray(tplTags) ? tplTags : [];

    return {
        id: storyId,
        title,
        type: "SLIDING",
        free: hasFree,
        publishedAt,
        genre,
        tags,
    };
}

/**
 * Map a GET `/home/sliding-play` row ({@link SlidingPlayRo}) to the card shape used by {@link SlidingCard}.
 * Story id drives `/puzzle/play-sliding/:storyId`.
 *
 * Response shape (new):
 *   { id, story, storySliding: { id, free, publishedAt }, sliding: { id, gridSize, difficultyLevel, state, startingLayout, createdAt, completedByUser },
 *     cloudUser, isCompleted, currentLayout, attemptsCount, … }
 */
export function slidingPlayRoToCardPuzzle(ro) {
    if (!ro || typeof ro !== "object") return null;
    const story =
        ro.story ?? ro.slidingStory ?? ro.slidingStoryGroup?.story ?? ro.sliding_story_group?.story ?? null;
    const storyId =
        (typeof ro.storyId === "string" && ro.storyId.trim()) ||
        (typeof ro.story_id === "string" && ro.story_id.trim()) ||
        (typeof story?.id === "string" && story.id.trim()) ||
        getSlidingGroupStoryId(ro.slidingStoryGroup) ||
        getSlidingGroupStoryId(ro.sliding_story_group) ||
        null;
    if (!storyId) return null;

    // New shape: sliding template details are on ro.sliding
    const slidingTpl = ro.sliding ?? null;

    const templates =
        ro.templates ??
        ro.slidingStoryGroup?.templates ??
        ro.sliding_story_group?.templates ??
        ro.slidingTemplates ??
        // Synthesise a single-template array from ro.sliding when present
        (slidingTpl ? [slidingTpl] : []);

    const title = getSlidingStoryTitle(story) || storyId;

    // New shape: free is authoritative on storySliding; legacy: scan templates
    const ss = ro.storySliding ?? null;
    const hasFree = ss && typeof ss === "object"
        ? ss.free === true
        : Array.isArray(templates) ? templates.some((t) => t?.free === true) : false;

    // New shape: publishedAt on storySliding; legacy: template or story
    const publishedAt =
        (ss?.publishedAt ?? ss?.published_at) ??
        story?.publishedAt ??
        story?.published_at ??
        (Array.isArray(templates) ? templates[0]?.publishedAt : null) ??
        ro.updatedAt ??
        ro.updated_at ??
        null;

    const genre =
        story?.topic?.genre?.name ??
        (Array.isArray(templates) ? templates[0]?.topic?.genre?.name : null) ??
        "default";
    const tplTags = Array.isArray(templates) ? templates[0]?.tags : null;
    const tags = Array.isArray(tplTags) ? tplTags : [];

    const completed =
        ro.isCompleted === true ||
        ro.is_completed === true ||
        ro.completed === true;

    const curLayout = Array.isArray(ro.currentLayout)
        ? ro.currentLayout
        : Array.isArray(ro.current_layout)
          ? ro.current_layout
          : null;

    // gridSize and difficultyLevel: prefer ro.sliding (new shape), fall back to first template
    const gridSize =
        slidingTpl?.gridSize ??
        (Array.isArray(templates) ? templates[0]?.gridSize : null) ??
        null;
    const difficultyLevel =
        slidingTpl?.difficultyLevel ??
        (Array.isArray(templates) ? templates[0]?.difficultyLevel : null) ??
        null;
    const startingLayout =
        slidingTpl?.startingLayout ?? slidingTpl?.starting_layout ??
        (Array.isArray(templates) ? (templates[0]?.startingLayout ?? templates[0]?.starting_layout) : null) ??
        null;

    return {
        id: storyId,
        title,
        type: "SLIDING",
        free: hasFree,
        isPremium: !hasFree && Array.isArray(templates) && templates.length > 0,
        publishedAt,
        genre,
        tags,
        story,
        // Disabled state from storySliding
        ...(ss?.state === "DISABLED" ? { state: "DISABLED", disabled: true } : {}),
        ...(ss?.disabledAt ?? ss?.disabled_at ? { disabledAt: new Date(ss.disabledAt ?? ss.disabled_at).getTime() } : {}),
        // Expose gridSize + difficultyLevel at top level so PuzzleCardBase renders the difficulty chip
        ...(gridSize != null ? { gridSize } : {}),
        ...(difficultyLevel != null ? { difficultyLevel } : {}),
        ...(Array.isArray(startingLayout) ? { startingLayout } : {}),
        slidingPlay: {
            isCompleted: completed,
            startedAt: ro.startedAt ?? ro.started_at ?? null,
            finishedAt: ro.finishedAt ?? ro.finished_at ?? null,
            updatedAt: ro.updatedAt ?? ro.updated_at ?? null,
            ...(curLayout ? { currentLayout: curLayout } : {}),
        },
    };
}

export function getLocalizedSlidingStoryBody(story, contentLang) {
    if (!story || typeof story !== "object") return "";
    const lang = String(contentLang || "english").toLowerCase();
    const preferFr = lang === "french" || lang === "fr";
    const fr =
        story.french?.text ??
        story.fr?.text ??
        (typeof story.french === "string" ? story.french : null) ??
        (typeof story.fr === "string" ? story.fr : null);
    const en =
        story.english?.text ??
        story.en?.text ??
        (typeof story.english === "string" ? story.english : null) ??
        (typeof story.en === "string" ? story.en : null);
    const frStr = typeof fr === "string" ? fr.trim() : "";
    const enStr = typeof en === "string" ? en.trim() : "";
    if (preferFr && frStr) return frStr;
    if (enStr) return enStr;
    return frStr || enStr || "";
}
